import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import TableSection from '@sections/transactions/table';
import classNames from 'classnames/bind';
import FilterSection from '@sections/transactions/filter';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiTransactionsBusinessService, apiTransactionsService } from '@api';
import {
    TTransactionLite,
    TTransactionsFilter
} from '@xeppt/xeppt-sdk/types/transaction';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { ENewNotificationType } from '@enum';
import { routes } from '@const/routes';
import Button from '@components/common/button';
import { useIsFirstRender } from '@mantine/hooks';
import { EUserType, TTransaction } from '@xeppt/xeppt-sdk/types';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useNavigate } from 'react-router-dom';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import Typography from '@components/common/typography';
import { TPaymentIntent } from '@xeppt/xeppt-sdk/types/payment-intent';
import { useDebounceValue } from 'usehooks-ts';
import type { TFindTransactionsFilterWithSearch } from 'src/types/reference_code';

const cx = classNames.bind(styles);

const TransactionsLayout = () => {
    const [filter, setFilter] = useState<TTransactionsFilter>({
        action: undefined,
        date_from: undefined,
        date_to: undefined
    });
    const navigate = useNavigate();
    const [id, intent] = useQueryParams(['id', 'intent']);
    const { onOpen, changeModalData } = useModalContext();
    const isFirstRender = useIsFirstRender();
    const [activeTab, setActiveTab] = useState<'transactions' | 'requests'>('transactions');
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 300);
    // Never forward an empty string as `search` — the backend's OR-group
    // matches it as a literal (rather than "no filter"), which would return
    // zero rows instead of the unfiltered list. `undefined` is dropped from
    // the JSON body entirely, correctly signaling "no search filter".
    const effectiveSearch = debouncedSearch.length >= 3 ? debouncedSearch : undefined;
    const [cursor, setCursor] = useState<Date>();
    // Business (merchant) wallets page through POST /transactions/business
    // instead — a separate cursor because its response cursor is a raw
    // RFC3339 string (see transactions_business_service.ts's PAGINATION
    // note), never a Date, unlike `cursor` above.
    const [businessCursor, setBusinessCursor] = useState<string>();
    const [intentCursor, setIntentCursor] = useState<Date>();
    const loader = useRef(null);
    const [transactions, setTransactions] = useState<TTransactionLite[]>([]);
    const [intents, setIntents] = useState<TPaymentIntent[]>([]);
    const [trigger, setTrigger] = useState(0);
    const { handleBack } = useNavigationContext();
    const [triggerClearFilter, setTriggerClearFilters] = useState(0);
    const { submitLocale } = useLocales();
    const [isOpen, setIsOpen] = useState(false);
    const { user } = useUserContext();
    // Mirrors the dashboard mini-tab's exact guard
    // (src/UI/sections/dashboard/transactions/index.tsx): only a LOADED,
    // non-individual user is a business wallet. `!!user` first is required —
    // `undefined !== EUserType.INDIVIDUAL` is otherwise true, which would
    // misroute an unresolved user to the business endpoint for one render.
    const isBusinessUser = !!user && user.type !== EUserType.INDIVIDUAL;

    const {
        data: intentsResponse,
        isLoading: isIntentsLoading,
        handleRequest: refetchIntents
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) =>
            apiTransactionsService.getPaymentIntents({
                cursor: request?.isNew
                    ? request?.date_to || new Date()
                    : intentCursor || new Date(),
                limit: 100
            }),
        condition: activeTab === 'requests',
        deps: [activeTab],
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setIntents((state) => [...state, ...data.data]);
                setIntentCursor(data.cursor);
            } else {
                setIntents(data?.data || []);
                setIntentCursor(data.cursor);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION
    });
    // Individual (consumer) wallets: the generic, cursor-based endpoint,
    // unchanged. `condition` now also requires a resolved INDIVIDUAL user
    // (mirrors the mini-tab) so this never fires for a business wallet, and
    // never fires for an unresolved user either — see isBusinessUser above.
    const {
        data: transactionsResponse,
        isLoading: isTransactionsLoading,
        handleRequest: fetchIndividualTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiTransactionsService.findTransactions(
                {
                    date_to: request?.date_to,
                    date_from: request?.date_from,
                    action: request?.action?.length === 0 ? undefined : request?.action,
                    method: request?.method?.length === 0 ? undefined : request?.method,
                    type: request?.type?.length === 0 ? undefined : request?.type,
                    search: request?.search
                } as TFindTransactionsFilterWithSearch,
                {
                    cursor: request?.isNew ? (request?.date_to || new Date()) : cursor,
                    limit: 100
                }
            );
        },
        condition: activeTab === 'transactions' && user?.type === EUserType.INDIVIDUAL,
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
                setCursor(data.cursor);
            } else {
                setTransactions(data?.data || []);
                setCursor(data.cursor);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION
    });

    // Business (merchant) wallets: POST /transactions/business, the
    // corrected partition (excludes Sales and External requests) — repoints
    // this "View all" page onto the same source the dashboard mini-tab
    // already uses. Keyset-paginated the same way as the generic endpoint
    // above: `businessCursor` omitted on a reset ("isNew") fetch seeds a
    // fresh "now" sentinel (NOT an omitted cursor — omitting it entirely
    // would engage the endpoint's offset mode instead, whose response never
    // carries a continuation cursor; see the PAGINATION note in
    // transactions_business_service.ts), then threaded back in verbatim —
    // as the STRING the backend returned, never Date-parsed — on every
    // subsequent "load more".
    const {
        data: businessTransactionsResponse,
        isLoading: isBusinessTransactionsLoading,
        handleRequest: fetchBusinessTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiTransactionsBusinessService.listTransactions({
                date_to: request?.date_to,
                date_from: request?.date_from,
                action: request?.action?.length === 0 ? undefined : request?.action,
                method: request?.method?.length === 0 ? undefined : request?.method,
                type: request?.type?.length === 0 ? undefined : request?.type,
                search: request?.search,
                cursor: request?.isNew ? (request?.date_to || new Date()) : businessCursor
            });
        },
        condition: activeTab === 'transactions' && isBusinessUser,
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
            } else {
                setTransactions(data?.data || []);
            }
            setBusinessCursor(data.cursor);
        },
        notificationType: ENewNotificationType.TRANSACTION
    });

    // Combined view over whichever branch is active — the trigger effect and
    // the loading prop below read these instead of picking a source inline.
    const isActiveTransactionsLoading = isBusinessUser
        ? isBusinessTransactionsLoading
        : isTransactionsLoading;
    const activeTransactionsTotal = isBusinessUser
        ? businessTransactionsResponse?.total
        : transactionsResponse?.total;

    // Single entry point every call site below keeps using unchanged — only
    // the source swaps based on user type. Skipping while `user` is
    // unresolved (rather than defaulting to the individual endpoint) means
    // an unresolved user is never routed to the business endpoint AND a
    // business user's first paint never briefly shows the unfiltered
    // generic-endpoint mix — the exact bug this repoint fixes. The `user`
    // dependency added to the mount/search effect below re-fires this once
    // user resolves.
    const handleGetTransactions = (request?: any) => {
        if (!user) return;
        if (isBusinessUser) {
            fetchBusinessTransactions(request);
        } else {
            fetchIndividualTransactions(request);
        }
    };

    useEffect(() => {
        const loadItems = () => {
            if (
                activeTab === 'transactions' &&
                transactions.length < (activeTransactionsTotal || 0) &&
                !isActiveTransactionsLoading
            ) {
                handleGetTransactions({ ...filter, search: effectiveSearch });
            }
            if (
                activeTab === 'requests' &&
                intents.length < (intentsResponse?.total || 0) &&
                !isIntentsLoading
            ) {
                refetchIntents();
            }
        };

        loadItems();
    }, [trigger]);

    const handleObserver = useCallback((entries: any) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        if (!isFirstRender) {
            if (activeTab === 'requests') {
                refetchIntents({ isNew: true });
            } else {
                handleGetTransactions({ ...filter, search: effectiveSearch, isNew: true });
            }
        }
    }, [activeTab]);

    // Search only applies to the transactions tab (findTransactions) — external
    // requests use a separate endpoint the backend hasn't added search to. The
    // input itself is hidden on that tab (see TableSection), so this is a
    // defensive guard rather than the primary gate.
    useEffect(() => {
        if (activeTab !== 'transactions') return;
        if (debouncedSearch.length >= 3 || debouncedSearch.length === 0) {
            setCursor(undefined);
            setBusinessCursor(undefined);
            setTransactions([]);
            handleGetTransactions({ ...filter, search: effectiveSearch, isNew: true });
        }
        // `user` re-fires this once user context resolves after mount — see
        // handleGetTransactions' guard above; without it, a business user
        // whose context resolves after this effect's first run would stay
        // stuck on whatever fired (or didn't) before resolution.
    }, [debouncedSearch, activeTab, user]);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const handleViewTransaction = (transaction: TTransaction) => {
        changeModalData({
            transaction,
            refetchTransactions: () =>
                handleGetTransactions({ ...filter, search: effectiveSearch, isNew: true })
        });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION,
            onClose: () => {
                if (id) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    const handleViewIntent = (transaction: TPaymentIntent) => {
        changeModalData({
            transaction,
            refetchTransactions: () => refetchIntents({ ...filter, isNew: true })
        });
        onOpen({
            modalId: modalIds.VIEW_EXTERNAL_TRANSACTION,
            onClose: () => {
                if (intent) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    useApiQuery({
        method: () => apiTransactionsService.getTransactionById(id || ''),
        condition: !!id,
        deps: [id],
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    useApiQuery({
        method: () => apiTransactionsService.getPaymentIntentById(id || ''),
        condition: !!intent,
        deps: [intent],
        onSuccess: (data) => {
            handleViewIntent(data);
        }
    });

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <div className={cx([styles.left, { isOpen }])}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.dashboard)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">Transactions</Typography>
                    </div>
                    <TableSection
                        triggerClearFilter={() => setTriggerClearFilters((prev) => prev + 1)}
                        transactions={activeTab === 'requests' ? intents : transactions}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        isTransactionsLoading={isActiveTransactionsLoading || isIntentsLoading}
                        onChangeTab={(val) => setActiveTab(val)}
                        activeTab={activeTab}
                        search={search}
                        onSearch={(val) => setSearch(val)}
                        filter={filter}
                        refetchTransactions={() => {
                            if (activeTab === 'requests') {
                                refetchIntents({ isNew: true });
                            } else {
                                handleGetTransactions({
                                    ...filter,
                                    search: effectiveSearch,
                                    isNew: true
                                });
                            }
                        }}
                        onChangeFilter={(value) => {
                            setCursor(undefined);
                            setBusinessCursor(undefined);
                            setIntentCursor(undefined);
                            setTransactions([]);
                            setIntents([]);
                            setFilter(value);
                            if (activeTab === 'transactions') {
                                handleGetTransactions({
                                    ...value,
                                    search: effectiveSearch,
                                    isNew: true
                                });
                            } else {
                                refetchIntents({ ...value, isNew: true });
                            }
                        }}
                    />
                    <div ref={loader} />
                </div>
                {isOpen && <div className={styles.overlay} onClick={() => setIsOpen(false)} />}
                <div className={cx([styles.right, { isOpen }])}>
                    <FilterSection
                        activeTab={activeTab}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        triggerClearFilter={triggerClearFilter}
                        onChangeFilter={(value) => {
                            setCursor(undefined);
                            setBusinessCursor(undefined);
                            setIntentCursor(undefined);
                            setTransactions([]);
                            setIntents([]);
                            setFilter(value);
                            if (activeTab === 'transactions') {
                                handleGetTransactions({
                                    ...value,
                                    search: effectiveSearch,
                                    isNew: true
                                });
                            } else {
                                refetchIntents({ ...value, isNew: true });
                            }
                        }}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default TransactionsLayout;
